import { Property } from "../types/property-types";
import { School, SchoolSearchArgs } from "../types/school-types";
import { getSchools } from "../data/great-schools/schools-fetcher";
import { greatSchoolsMock } from "../data/great-schools/__data__/great-schools-mock";

/**
 * Retrieves a list of nearby schools for a given property based on specified search criteria.
 *
 * @param {Property} property - The property for which to find nearby schools. Must include location with latitude and longitude.
 * @param {SchoolSearchArgs} schoolSearchArgs - The search arguments including limit, distance, and other filtering options.
 * @returns {Promise<School[]>} A promise that resolves to an array of schools that match the search criteria.
 *
 * @remarks
 * - If the property's location is not specified, an empty array is returned.
 * - The function filters schools based on rating availability and sorts them by distance.
 * - The result is limited to the specified render limit.
 * - If no schools are found, a mock list of schools (`greatSchoolsMock`) is returned.
 */
export const getPropertyNearbySchools = async (
  property: Property,
  schoolSearchArgs: SchoolSearchArgs
): Promise<School[]> => {
  if (!property.location?.latitude || !property.location?.longitude) {
    return [];
  }

  const { limit, distance } = schoolSearchArgs;

  const schools = await getSchools(
    property.location.latitude.toString(),
    property.location.longitude.toString(),
    limit,
    distance
  ).then((schools) => {
    if (schools) {
      return schools
        .filter(
          filterSchoolsByRatingAvailability(
            schoolSearchArgs.showSchoolsWithoutRating
          )
        )
        .sort(sortSchoolsByDistance)
        .slice(0, schoolSearchArgs.renderLimit);
    } else {
      return greatSchoolsMock;
    }
  });

  return schools;
};

/**
 * Sorts an array of schools by their distance in ascending order.
 *
 * @param a - The first school to compare.
 * @param b - The second school to compare.
 * @returns A negative number if the distance of `a` is less than the distance of `b`,
 *          zero if the distances are equal, or a positive number if the distance of `a` is greater than the distance of `b`.
 */
const sortSchoolsByDistance = (a: School, b: School): number => {
  // compact comparison
  return a.distance - b.distance;
};

/**
 * Filters schools based on the availability of their rating.
 *
 * @param showSchoolsWithoutRating - A boolean flag indicating whether to include schools without a rating.
 * @returns A function that takes a `School` object and returns a boolean indicating whether the school should be included based on the rating availability.
 */
const filterSchoolsByRatingAvailability =
  (showSchoolsWithoutRating: boolean) =>
  (school: School): boolean => {
    if (showSchoolsWithoutRating) {
      return true;
    }

    if (school.rating) {
      return true;
    }

    return false;
  };
